import React, { FC, memo, useMemo } from 'react'
import { Formik, FormikProps } from 'formik'
import { Button, Col, Form, Row, Select, Spin } from 'antd'
import { FormAlert } from '@shared/modules/form/styled'

import { FormOptions } from '@shared/modules/form/types'
import { TextField } from '@shared/modules/form/fields/text'
import { DateField } from '@shared/modules/form/fields/date'
import { SelectField } from '@shared/modules/form/fields/select'
import { AppointmentProductFormData } from '../types'
import { useAppointmentProductForm } from '../hooks'
import { useWarehouseList } from '../../warehouses/hooks'
import { useProductList } from '../../products/hooks'
import { useDoctorList } from '../../doctors/hooks/use-list'
import { useAuth } from '../../me/hooks'

type FormProps = FormikProps<AppointmentProductFormData>

const FormikComponent = memo<FormProps>(({ isSubmitting, handleSubmit, status }) => {
  const { can } = useAuth()
  const warehouseCollection = useWarehouseList()
  const productCollection = useProductList()
  const doctorCollection = useDoctorList()

  const productOptions = useMemo(
    () =>
      productCollection.data.map((product) => (
        <Select.Option key={product.id} value={product.id.toString()}>
          {product.name}
        </Select.Option>
      )),
    [productCollection.data],
  )

  const warehouseOptions = useMemo(
    () =>
      warehouseCollection.data.map((warehouse) => (
        <Select.Option key={warehouse.id} value={warehouse.id.toString()}>
          {warehouse.name}
        </Select.Option>
      )),
    [warehouseCollection.data],
  )

  const doctorOptions = useMemo(
    () =>
      doctorCollection.data.map((doctor) => (
        <Select.Option key={doctor.id} value={doctor.id.toString()}>
          {doctor.firstname} {doctor.lastname} {doctor.clinic && `- ${doctor.clinic.name}`}
        </Select.Option>
      )),
    [doctorCollection.data],
  )

  return (
    <Form layout='vertical'>
      <FormAlert $visible={!!status} message={status?.message} type={status?.type} />
      <Row gutter={[12, 12]}>
        {can(127) && (
          <Col xs={24} md={12}>
            <SelectField
              name='warehouseId'
              item={{ label: 'Anbar' }}
              input={{ placeholder: 'Anbar daxil edin', loading: warehouseCollection.loading }}
            >
              {warehouseOptions}
            </SelectField>
          </Col>
        )}
        <Col xs={24} md={12}>
          <SelectField name='productId' item={{ label: 'Məhsul' }} input={{ placeholder: 'Məhsul daxil edin', loading: productCollection.loading }}>
            {productOptions}
          </SelectField>
        </Col>
        <Col xs={24} md={12}>
          <TextField name='quantity' item={{ label: 'Miqdar' }} input={{ placeholder: 'Miqdar daxil edin' }} format='decimal' />
        </Col>
        <Col xs={24} md={12}>
          <TextField name='discount' item={{ label: 'Endirim məbləği' }} input={{ placeholder: 'Endirim məbləği daxil edin' }} format='decimal' />
        </Col>
        <Col xs={24}>
          <SelectField name='doctorId' item={{ label: 'Həkim' }} input={{ placeholder: 'Həkim daxil edin', loading: doctorCollection.loading }}>
            {doctorOptions}
          </SelectField>
        </Col>
        <Col xs={24} md={12}>
          <DateField name='consumptionDate' item={{ label: 'Xərclənmə tarixi' }} input={{ placeholder: 'Xərclənmə tarixi daxil edin' }} />
        </Col>
      </Row>
      <Button onClick={() => handleSubmit()} type='primary' loading={isSubmitting} block={true}>
        Təsdiqlə
      </Button>
    </Form>
  )
})

export const AppointmentProductForm: FC<FormOptions> = ({ type, redirectUrl }) => {
  const { initialValues, ...form } = useAppointmentProductForm({ redirectUrl, type })

  return (
    <Spin spinning={false}>
      <Formik {...form} enableReinitialize={true} initialValues={initialValues}>
        {(formikProps) => <FormikComponent {...formikProps} />}
      </Formik>
    </Spin>
  )
}
